import React, { useEffect, useRef } from 'react';
import { DropdownItem, SparkIcon } from '@spark';

/**
 * The `{{` / `/`-triggered insert menu (also shown on text selection):
 * Dataset columns (name + current record value + type icon) and Custom
 * fields, plus the new-field footer. Rendered by the modal at the caret /
 * selection position inside the editor wrapper.
 *
 * `activeIndex` is the keyboard highlight over the flattened item list —
 * columns first, then custom fields (the modal's ArrowUp/Down handler moves
 * it and Enter picks the highlighted item).
 */
const InsertFieldsMenu = ({
    columns,
    customFields,
    activeIndex = -1,
    onPickColumn,
    onPickCustomField,
    footer,
}) => {
    const customEntries = Object.entries(customFields || {});
    const columnCount = (columns || []).length;
    const bodyRef = useRef(null);

    // Keep the keyboard-highlighted row visible while arrowing through.
    // Scrolls ONLY the menu body — scrollIntoView would also scroll the
    // modal's body, stranding the whole modal in blank space whenever the
    // popover pokes outside it.
    useEffect(() => {
        if (activeIndex < 0) return;
        const body = bodyRef.current;
        const item = body?.querySelectorAll('.spark-dropdown-item')?.[activeIndex];
        if (!body || !item) return;
        const bodyRect = body.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        if (itemRect.top < bodyRect.top) {
            body.scrollTop += itemRect.top - bodyRect.top;
        } else if (itemRect.bottom > bodyRect.bottom) {
            body.scrollTop += itemRect.bottom - bodyRect.bottom;
        }
    }, [activeIndex]);

    return (
        <div className="cw-pm-insert-menu spark-dropdown" role="menu">
            <div className="cw-pm-insert-menu__body" ref={bodyRef}>
                {columnCount > 0 && (
                    <div className="spark-dropdown-search-section">
                        <div className="spark-dropdown-search-section-title" role="presentation">
                            Dataset columns
                        </div>
                        {columns.map(({ name, value, textual }, index) => (
                            <DropdownItem
                                key={name}
                                label={name}
                                state={index === activeIndex ? 'hover' : 'default'}
                                prefix={
                                    <SparkIcon
                                        slug={textual ? 'text' : 'code-dots'}
                                        size="small"
                                        className="cw-pm-insert-menu__type"
                                    />
                                }
                                suffix={
                                    value ? (
                                        <span className="cw-pm-insert-menu__value">{value}</span>
                                    ) : null
                                }
                                onClick={() => onPickColumn(name)}
                            />
                        ))}
                    </div>
                )}
                {customEntries.length > 0 && (
                    <div className="spark-dropdown-search-section">
                        <div className="spark-dropdown-search-section-title" role="presentation">
                            Custom fields
                        </div>
                        {customEntries.map(([name, originalText], index) => (
                            <DropdownItem
                                key={name}
                                label={name}
                                state={columnCount + index === activeIndex ? 'hover' : 'default'}
                                prefix={
                                    <SparkIcon
                                        slug="edit"
                                        size="small"
                                        className="cw-pm-insert-menu__type"
                                    />
                                }
                                suffix={
                                    originalText ? (
                                        <span className="cw-pm-insert-menu__value">
                                            {originalText}
                                        </span>
                                    ) : null
                                }
                                onClick={() => onPickCustomField(name)}
                            />
                        ))}
                    </div>
                )}
            </div>
            {footer ? <div className="cw-pm-insert-menu__footer">{footer}</div> : null}
        </div>
    );
};

export default InsertFieldsMenu;
